import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Reservation } from '@turizi/shared';
import { navigate, RESERVATIONS_PATH } from '@/lib/navigation';
import { formatDayShort } from '@/lib/format-utils';
import { IconGitBranch, IconGitMerge } from '@tabler/icons-react';
import { ReservationClient, ReservationPaymentStatusSmall, ReservationStatusBadge } from '../reservation-ui';
import ReservationCoordinatorSelector from '../service-booking/reservation-coordinator-selector';
import ReservationPriceTotals from '../reservation-price-totals';
import { Button, CopyButton } from '@mantine/core';

export interface ReservationCardDisplayOptions {
    showClientName?: boolean;
    showPaymentStatus?: boolean;
    showPrice?: boolean;
    showExternalCode?: boolean;
    editable?: boolean;
    [key: string]: any;
}

export interface ReservationCardHeaderProps {
    reservation: Reservation;
    activeDisplayOptions?: ReservationCardDisplayOptions;
}

const DEFAULT_DISPLAY_OPTIONS: ReservationCardDisplayOptions = {
    showClientName: true,
    showPaymentStatus: false,
    showPrice: false,
    showExternalCode: true,
    editable: false,
};

export default function ReservationCardHeader({
    reservation,
    activeDisplayOptions,
}: ReservationCardHeaderProps): JSX.Element {
    const { t } = useTranslation();
    const options = { ...DEFAULT_DISPLAY_OPTIONS, ...activeDisplayOptions };
    const editable = options.editable || false;

    const handleHeaderClick = (e: React.MouseEvent) => {
        const targetPath = `${RESERVATIONS_PATH}/${reservation.id}`;
        if (e.metaKey || e.ctrlKey) {
            window.open(targetPath, '_blank');
        } else {
            navigate(targetPath);
        }
    };

    return (
        <div
            onClick={handleHeaderClick}
            className="group flex flex-wrap gap-6 items-center justify-between w-full px-4 py-2.5 bg-slate-800 text-white rounded-t-md cursor-pointer"
        >
            <div className="flex flex-wrap gap-2.5 items-center">
                {/* ID & Reservation Dates */}
                <div className="flex items-center gap-2">
                    <ReservationStatusBadge showText={false} reservation={reservation} className="text-white" />
                    <div className="uppercase group-hover:text-blue-300 text-white font-bold flex items-center gap-2 transition-colors">
                        #{reservation.id}
                    </div>
                    <div className="whitespace-nowrap text-xs font-semibold group-hover:text-blue-300 text-slate-100 bg-slate-700/80 border border-slate-500 px-2.5 py-1 rounded-md h-fit shadow-2xs transition-colors">
                        {formatDayShort(reservation.start_day)} - {formatDayShort(reservation.end_day)}
                    </div>
                </div>

                {/* Client Name */}
                {options.showClientName && (
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-slate-700/80 border border-slate-600/90 text-slate-100 px-2.5 py-1 rounded-md text-xs font-bold whitespace-nowrap shadow-2xs"
                    >
                        <ReservationClient
                            reservation={reservation}
                            icon={true}
                            className="text-sky-300 font-bold hover:text-sky-100 transition-colors"
                        />
                    </div>
                )}

                {/* Reservation Coordinator Selector */}
                <div onClick={(e) => e.stopPropagation()}>
                    <ReservationCoordinatorSelector
                        reservation={reservation}
                        size="xs"
                        editable={editable}
                    />
                </div>

                {/* Parent Reservation Link */}
                {(reservation.parent_id || reservation.parent) && (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Link
                            to={`${RESERVATIONS_PATH}/${reservation.parent_id || reservation.parent?.id}`}
                            className="bg-slate-700/80 border border-slate-600/90 hover:bg-slate-700 text-slate-200 hover:text-white px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap flex items-center gap-1.5 transition-colors shadow-2xs"
                            title={t('original_reservation')}
                        >
                            <IconGitBranch size={13} className="text-teal-400" />
                            <span className="text-slate-300 font-medium text-[11px]">{t('original_reservation')}:</span>
                            <span className="font-bold text-sky-300 hover:text-sky-100">
                                #{reservation.parent_id || reservation.parent?.id}
                            </span>
                        </Link>
                    </div>
                )}

                {/* Derived Child Reservations Links */}
                {reservation.child_reservations && reservation.child_reservations.length > 0 && (
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-slate-700/80 border border-slate-600/90 px-2.5 py-1 rounded-md text-xs font-semibold text-slate-200 whitespace-nowrap flex items-center gap-1.5 shadow-2xs"
                    >
                        <IconGitMerge size={13} className="text-teal-400" />
                        <span className="text-slate-300 font-medium text-[11px]">{t('child_reservations')}:</span>
                        <div className="flex items-center gap-1.5">
                            {reservation.child_reservations.map((child) => (
                                <Link
                                    key={child.id}
                                    to={`${RESERVATIONS_PATH}/${child.id}`}
                                    className="font-bold text-sky-300 hover:text-sky-100 transition-colors"
                                >
                                    #{child.id}
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
                {reservation.observations && (
                    <div className='text-xs px-1 py-0.5 text-gray-800 rounded text-wrap border border-red-100  bg-red-50'>
                        {reservation.observations}
                    </div>
                )}
                {options.showExternalCode && reservation.external_code && (
                    <div onClick={(e) => e.stopPropagation()}>
                        <CopyButton value={reservation.external_code} timeout={2000}>
                            {({ copied, copy }) => (
                                <Button
                                    color='blue'
                                    variant='outline'
                                    size='xs'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        copy();
                                    }}
                                    className="text-xs px-1 py-0.5 bg-blue-50 hover:bg-blue-100 text-gray-800 rounded text-wrap border border-blue-100 cursor-pointer transition-colors"
                                >
                                    {copied ? t('copied') : reservation.external_code}
                                </Button>
                            )}
                        </CopyButton>
                    </div>
                )}
            </div>


            {/* Totals & Status */}
            {options.showPrice && (
                <div className="flex gap-2 items-center justify-end">
                    <ReservationPaymentStatusSmall reservation={reservation} className='flex gap-4 text-white' />
                    <ReservationPriceTotals
                        size='xs'
                        reservation={reservation}
                        showStatus={options.showPaymentStatus}
                    />
                </div>
            )}
        </div>
    );
}
